import type {
  SpraySummary,
  OperatorLicenseRow,
  MachineCalibrationRow,
} from '../services/sprayDashboardService';

const DAY_MS = 86400e3;

export const LICENSE_LEAD_DAYS     = 30;
export const CALIBRATION_LEAD_DAYS = 14;

export interface SprayKpis {
  activeProgramsCount:    number;
  runningSessions:        number;
  upcomingCount:          number;
  operatorsExpiringCount: number;
  machinesDueCount:       number;
}

export interface LicenseAlert {
  _id: string;
  name: string;
  daysUntilExpiry: number;
  expired: boolean;
}

export interface CalibrationAlert {
  _id: string;
  name: string;
  daysUntilDue: number;
  overdue: boolean;
  neverCalibrated: boolean;
}

export function computeSprayKpis(summary: SpraySummary, now: Date): SprayKpis {
  return {
    activeProgramsCount:    summary.activePrograms.count,
    runningSessions:        summary.activePrograms.longRunningSessions.length,
    upcomingCount:          summary.upcomingSessions.length,
    operatorsExpiringCount: classifyLicenseExpiry(summary.operatorLicenses, now).length,
    machinesDueCount:       classifyCalibrationDue(summary.machineCalibrations, now).length,
  };
}

export function classifyLicenseExpiry(rows: OperatorLicenseRow[], now: Date, leadDays = LICENSE_LEAD_DAYS): LicenseAlert[] {
  const out: LicenseAlert[] = [];
  for (const r of rows) {
    if (!r.licenseExpiryDate) continue;
    const expiry = new Date(r.licenseExpiryDate);
    const days = Math.floor((expiry.getTime() - now.getTime()) / DAY_MS);
    if (days > leadDays) continue;
    out.push({ _id: r._id, name: r.name, daysUntilExpiry: days, expired: days < 0 });
  }
  out.sort((a, b) => a.daysUntilExpiry - b.daysUntilExpiry);
  return out;
}

export function classifyCalibrationDue(rows: MachineCalibrationRow[], now: Date, leadDays = CALIBRATION_LEAD_DAYS): CalibrationAlert[] {
  const out: CalibrationAlert[] = [];
  for (const r of rows) {
    if (!r.nextCalibrationDue) {
      // Never calibrated — flag as immediately due.
      out.push({ _id: r._id, name: r.name, daysUntilDue: 0, overdue: true, neverCalibrated: true });
      continue;
    }
    const due = new Date(r.nextCalibrationDue);
    const days = Math.floor((due.getTime() - now.getTime()) / DAY_MS);
    if (days > leadDays) continue;
    out.push({ _id: r._id, name: r.name, daysUntilDue: days, overdue: days < 0, neverCalibrated: false });
  }
  out.sort((a, b) => a.daysUntilDue - b.daysUntilDue);
  return out;
}

